import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlayersListWithBranch } from "@/components/Lists/PlayerBranchList";
import { Button, Dialog, Badge, Table, Spinner, Input } from "@/components/ui";
import { Sparkles, UserCheck, Plus, Download, Info } from "lucide-react";
import { PlayersServices } from "@/services";
import { useSocialCoachSession, userFullName, exportDataToCsv } from "@/utils";

interface OwnProps {
  selectedPlayers: number[];
  eligiblePlayerIds?: number[];
  isInclude?: boolean;
  disabled: boolean;
  onChange: (selectedPlayers: number[]) => void;
  autoEnroll?: boolean;
  onAutoEnrollChange?: (autoEnroll: boolean) => void;
  isAutoPost?: boolean;
}

type Props = OwnProps;
type EnrollmentMode = "auto" | "select";

const AutoSubscriptionsFC: React.FC<Props> = ({
  eligiblePlayerIds = [],
  selectedPlayers = [],
  isInclude = true,
  disabled,
  onChange,
  autoEnroll,
  onAutoEnrollChange,
  isAutoPost = false,
}) => {
  const [openSelector, setOpenSelector] = useState<boolean>(false);
  const [openEligibleList, setOpenEligibleList] = useState<boolean>(false);
  const [eligibleSearch, setEligibleSearch] = useState<string>("");

  const { coachId } = useSocialCoachSession();

  // In-memory cache to preserve player selections during mode transitions
  const [localSelectedPlayers, setLocalSelectedPlayers] = useState<number[]>(selectedPlayers);

  // Staging area state specifically for transactional modal edits
  const [tempSelectedPlayers, setTempSelectedPlayers] = useState<number[]>([]);

  // Initialize the local enrollmentMode state from parent props
  const [enrollmentMode, setEnrollmentMode] = useState<EnrollmentMode>(() => {
    if (autoEnroll === true) return "auto";
    return "select";
  });

  // Fetch eligible players full account details
  const { data: eligiblePlayers = [], isLoading: isLoadingEligible } = useQuery({
    queryKey: ["eligiblePlayers", coachId, eligiblePlayerIds],
    queryFn: async () => {
      if (!eligiblePlayerIds || eligiblePlayerIds.length === 0) return [];
      const response = await PlayersServices.searchSubscribedAllPlayersByTag(coachId!, {
        playerIds: eligiblePlayerIds,
      });
      return response.content || [];
    },
    enabled: !!coachId && !!eligiblePlayerIds && eligiblePlayerIds.length > 0 && openEligibleList,
  });

  // Sync state if props change (reinitialization)
  useEffect(() => {
    if (autoEnroll === true) {
      setEnrollmentMode("auto");
    } else {
      setEnrollmentMode("select");
    }
  }, [autoEnroll]);

  // Sync cache if external selectedPlayers props change while in 'select' mode
  useEffect(() => {
    if (enrollmentMode === "select") {
      setLocalSelectedPlayers(selectedPlayers);
    }
  }, [selectedPlayers, enrollmentMode]);

  // Clone active selections into temporary staging state when the dialog is opened
  useEffect(() => {
    if (openSelector) {
      setTempSelectedPlayers(localSelectedPlayers);
    }
  }, [openSelector, localSelectedPlayers]);

  const handleModeChange = (mode: EnrollmentMode) => {
    if (disabled) return;
    setEnrollmentMode(mode);

    if (mode === "auto") {
      onAutoEnrollChange?.(true);
      onChange([]); // Clear parent selected players since auto-enrolling everyone
    } else {
      onAutoEnrollChange?.(false);
      // Restore parent selected players from our in-memory local cache!
      onChange(localSelectedPlayers);
    }
  };

  const handleChangeSelectedPlayer = useCallback((playerIds: number[], checked: boolean) => {
    setTempSelectedPlayers(prev => {
      let newSelected = [...prev];
      if (checked) {
        playerIds.forEach(playerId => {
          if (!newSelected.includes(playerId)) {
            newSelected.push(playerId);
          }
        });
      } else {
        newSelected = newSelected.filter(pId => !playerIds.includes(pId));
      }
      return newSelected;
    });
  }, []);

  const handleSaveSelection = () => {
    setLocalSelectedPlayers(tempSelectedPlayers);
    onAutoEnrollChange?.(false); // Force autoEnroll to FALSE when explicitly saving custom selections
    onChange(tempSelectedPlayers); // Propagate to parent form only when explicitly saving!
    setOpenSelector(false);
  };

  const handleEnableAutoEnroll = () => {
    handleModeChange("auto");
    setOpenEligibleList(false);
  };

  // Filter eligible players list locally in-memory
  const filteredEligiblePlayers = useMemo(() => {
    if (!eligibleSearch.trim()) return eligiblePlayers || [];
    const query = eligibleSearch.toLowerCase();
    return (
      eligiblePlayers?.filter(player => {
        const name = userFullName(player.playerAccount).toLowerCase();
        const email = (player.playerAccount.email || "").toLowerCase();
        return name.includes(query) || email.includes(query);
      }) || []
    );
  }, [eligiblePlayers, eligibleSearch]);

  // Export eligible players list to CSV
  const handleExportEligible = () => {
    const formattedData = eligiblePlayers.map(player => ({
      Name: userFullName(player.playerAccount),
      Email: player.playerAccount.email || "",
      Status: "Eligible for Auto-Enrollment",
    }));
    exportDataToCsv("Eligible_Campaign_Players", formattedData);
  };

  const isSelectActive = enrollmentMode === "select";
  const isAutoActive = enrollmentMode === "auto";

  return (
    <div className="mt-4 space-y-6">
      {/* Header and Subheader */}
      <div className="space-y-1 select-none text-left">
        <h4 className="text-base font-bold text-foreground">Campaign Enrollment Method</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Choose how eligible players are enrolled in this campaign.
        </p>
      </div>

      {/* Radio Cards Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Enroll Specific Players Card */}
        <div
          onClick={() => handleModeChange("select")}
          className={`flex flex-col justify-between border rounded-xl p-5 bg-white shadow-xs hover:shadow-md cursor-pointer select-none transition-all duration-150 relative h-full ${
            isSelectActive
              ? "border-primary ring-1 ring-primary/25 bg-white"
              : "border-border/80 hover:border-slate-300 dark:hover:border-slate-700"
          } ${disabled ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}`}
        >
          <div className="space-y-3.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    isSelectActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  <UserCheck className="size-5" />
                </div>
                <span className="text-base font-bold text-foreground">Select Players to Enroll</span>
              </div>
              {localSelectedPlayers.length > 0 && (
                <Badge
                  variant="secondary"
                  onClick={e => {
                    e.stopPropagation();
                    setOpenSelector(true);
                  }}
                  className={`border-none text-xs font-semibold px-2 py-0.5 whitespace-nowrap transition-all duration-150 cursor-pointer ${
                    isSelectActive
                      ? "bg-primary/10 text-primary hover:bg-primary/20"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {localSelectedPlayers.length} Selected
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Choose which eligible Players should be enrolled in this campaign. Eligible Players who are not currently
              selected for enrollment can still view this Campaign and opt in when they want to participate.
            </p>
            <div className="pt-0.5">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setOpenSelector(true);
                }}
                disabled={disabled}
                className="h-8 text-xs font-bold px-3 rounded-lg border-slate-200 shadow-xs gap-1 hover:bg-slate-50 w-full"
              >
                <Plus className="size-3.5" />
                {localSelectedPlayers.length === 0 ? "Select Players" : "Manage Selected Players"}
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-end mt-4 border-t border-border/40 pt-3 h-8">
            <div
              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                isSelectActive ? "border-primary" : "border-slate-300"
              }`}
            >
              {isSelectActive && <div className="w-2 h-2 rounded-full bg-primary" />}
            </div>
          </div>
        </div>

        {/* Auto-Enroll Everyone Card */}
        <div
          onClick={() => handleModeChange("auto")}
          className={`flex flex-col justify-between border rounded-xl p-5 bg-white shadow-xs hover:shadow-md cursor-pointer select-none transition-all duration-150 relative h-full ${
            isAutoActive
              ? "border-primary ring-1 ring-primary/25 bg-white"
              : "border-border/80 hover:border-slate-300 dark:hover:border-slate-700"
          } ${disabled ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}`}
        >
          <div className="space-y-3.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    isAutoActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Sparkles className="size-5" />
                </div>
                <span className="text-base font-bold text-foreground">Auto-Enroll All Eligible Players</span>
              </div>
              {eligiblePlayerIds && eligiblePlayerIds.length > 0 && (
                <Badge
                  variant="secondary"
                  onClick={e => {
                    e.stopPropagation();
                    setOpenEligibleList(true);
                  }}
                  className={`border-none text-xs font-semibold px-2 py-0.5 whitespace-nowrap transition-all duration-150 cursor-pointer ${
                    isAutoActive
                      ? "bg-primary/10 text-primary hover:bg-primary/20"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {eligiblePlayerIds.length} Eligible
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Automatically enroll all current and future eligible Players to this campaign. Any new Players who meet
              the eligibility criteria in the future will be enrolled automatically.
            </p>
          </div>
          <div className="flex items-center justify-end mt-4 border-t border-border/40 pt-3 h-8">
            <div
              className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                isAutoActive ? "border-primary" : "border-slate-300"
              }`}
            >
              {isAutoActive && <div className="w-2 h-2 rounded-full bg-primary" />}
            </div>
          </div>
        </div>
      </div>

      {/* Players List Selection Dialog */}
      <Dialog open={openSelector} onOpenChange={setOpenSelector} size="3xl">
        <Dialog.Header>
          <Dialog.Title>Select Players to Enroll</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="p-6 flex flex-col max-h-[80vh] overflow-hidden">
          <div className="mb-4 bg-muted/20 p-3 rounded-lg border border-border/40">
            <div className={"flex flex-row gap-4 items-center"}>
              <Info className="size-4 text-muted-foreground" />
              <div className="text-sm  leading-relaxed">
                Select Players below to enroll them in this campaign. Eligible players who aren't selected can still see
                this campaign, but will have to enroll themselves in order to participate.
                <br />
                <em>Note: Only Players who meet this campaign's eligibility criteria are shown below.</em>
              </div>
            </div>
          </div>
          <PlayersListWithBranch
            disabled={disabled}
            selectedPlayers={tempSelectedPlayers}
            showState={true}
            onChangeSelectedPlayer={handleChangeSelectedPlayer}
            segmentedPlayers={eligiblePlayerIds}
            isInclude={isInclude}
            scrollable={true}
          />
        </Dialog.Content>
        <Dialog.Footer className="flex gap-2 justify-end">
          <Button variant="outline" type="button" onClick={() => setOpenSelector(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSaveSelection}>
            Confirm Selected Players
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* View Eligible Players List Dialog */}
      <Dialog open={openEligibleList} onOpenChange={setOpenEligibleList} size="xl">
        <Dialog.Header>
          <Dialog.Title>Eligible Players</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="p-6">
          <p className="text-sm text-muted-foreground leading-relaxed bg-muted/20 border border-border/40 p-4 rounded-xl mb-4 text-left">
            The Players listed below currently meet this campaign's eligibility criteria and can view the campaign. If{" "}
            <b>"Auto-Enroll All Eligible Players"</b> is enabled, they will all be enrolled automatically.
          </p>

          {/* Search and Export Toolbar */}
          <div className="flex items-center gap-2 mb-3 select-none">
            <Input
              placeholder="Search by name or email..."
              value={eligibleSearch}
              onChange={e => setEligibleSearch(e.target.value)}
              className="max-w-xs h-9 bg-background border border-border rounded-lg text-xs"
            />
            <div className="flex-1" />
            {eligiblePlayers && eligiblePlayers.length > 0 && (
              <Badge
                variant="secondary"
                className="bg-primary/10 text-primary hover:bg-primary/10 border-none text-xs font-extrabold px-2.5 h-9 rounded-lg flex items-center shrink-0"
              >
                {eligiblePlayers.length} Eligible
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleExportEligible}
              disabled={!eligiblePlayers || eligiblePlayers.length === 0}
              className="h-9 px-3 text-xs font-bold gap-1.5 rounded-lg border-slate-200 shrink-0"
            >
              <Download className="size-3.5" />
              Export CSV
            </Button>
          </div>

          <div className="border border-border rounded-lg overflow-hidden bg-background max-h-[300px] overflow-y-auto no-scrollbar">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head className="font-semibold text-left">Name</Table.Head>
                  <Table.Head className="font-semibold text-left">Email</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {isLoadingEligible ? (
                  <Table.Row>
                    <Table.Cell colSpan={2} className="text-center py-8 text-muted-foreground">
                      <Spinner className="size-5 inline-block mr-2" /> Loading eligible players...
                    </Table.Cell>
                  </Table.Row>
                ) : filteredEligiblePlayers?.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={2} className="text-center py-8 text-muted-foreground">
                      {eligibleSearch ? "No matching eligible players found." : "No eligible players found."}
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  filteredEligiblePlayers?.map(player => (
                    <Table.Row key={`eligible-player-${player.playerAccount.playerId}`} className="hover:bg-muted/30">
                      <Table.Cell className="align-middle text-sm font-semibold text-foreground text-left">
                        {userFullName(player.playerAccount)}
                      </Table.Cell>
                      <Table.Cell className="align-middle text-sm text-muted-foreground text-left">
                        {player.playerAccount.email}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </div>
        </Dialog.Content>
        <Dialog.Footer className="flex gap-2 justify-end">
          {isAutoActive ? (
            <Button type="button" onClick={() => setOpenEligibleList(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" type="button" onClick={() => setOpenEligibleList(false)}>
                Close
              </Button>
              <Button type="button" onClick={handleEnableAutoEnroll}>
                Auto-Enroll All {eligiblePlayerIds.length} Player{eligiblePlayerIds.length === 1 ? "" : "s"}
              </Button>
            </>
          )}
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};

export const AutoSubscriptions = AutoSubscriptionsFC;
